import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';

const DISCLAIMER_KEY = 'nauticoach_disclaimer_accepted';

const DisclaimerDialog = () => {
  const { t } = useTranslation('ports');
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      const accepted = localStorage.getItem(`${DISCLAIMER_KEY}_${user.id}`);
      if (!accepted) {
        setOpen(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!open) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasScrolledToBottom(true);
        }
      },
      { threshold: 1.0 }
    );

    // Small delay to let the dialog render
    const timer = setTimeout(() => {
      if (bottomRef.current) {
        observer.observe(bottomRef.current);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [open]);

  const handleAccept = () => {
    if (user) {
      localStorage.setItem(`${DISCLAIMER_KEY}_${user.id}`, 'true');
    }
    setOpen(false);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-lg [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-xl text-center">
            {t('disclaimer.title')}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4" ref={scrollRef}>
          <div className="space-y-4 text-sm text-muted-foreground">
            <section>
              <h3 className="font-semibold text-foreground mb-1">{t('disclaimer.section1Title')}</h3>
              <p>
                {t('disclaimer.section1Text')}
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1">{t('disclaimer.section2Title')}</h3>
              <p className="mb-2">
                {t('disclaimer.section2Intro')}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>{t('disclaimer.section2Item1')}</li>
                <li>{t('disclaimer.section2Item2')}</li>
                <li>{t('disclaimer.section2Item3')}</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1">{t('disclaimer.section3Title')}</h3>
              <p className="mb-2">{t('disclaimer.section3Intro')}</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>{t('disclaimer.section3Item1Bold')}</strong>{t('disclaimer.section3Item1Text')}</li>
                <li><strong>{t('disclaimer.section3Item2Bold')}</strong>{t('disclaimer.section3Item2Text')}</li>
                <li><strong>{t('disclaimer.section3Item3Bold')}</strong>{t('disclaimer.section3Item3Text')}</li>
                <li><strong>{t('disclaimer.section3Item4Bold')}</strong>{t('disclaimer.section3Item4Text')}</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1">{t('disclaimer.section4Title')}</h3>
              <p>
                {t('disclaimer.section4Text')}
              </p>
            </section>

            <div ref={bottomRef} className="h-1" />
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button
            onClick={handleAccept}
            disabled={!hasScrolledToBottom}
            className="w-full"
          >
            {hasScrolledToBottom
              ? t('disclaimer.acceptButton')
              : t('disclaimer.scrollPrompt')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DisclaimerDialog;
